// CompositeLayer - Visualizes both slope and aspect together
// Inherits common tile management logic from TerrainLayer
// Uses composite worker to compute both aspect and slope values
// Visualization: 8 directions × 4 slope ranges = 32 color combinations

import { calculateAspect, calculateSlope, calculateSobel, calculatePixelMeters, decodeDEM, isValidElevation } from '../../utils/terrainCalculations';
import TerrainLayer from './TerrainLayer';

class CompositeLayer extends TerrainLayer {
    /**
     * Get path to composite worker script
     */
    getWorkerPath () {
        return '/static/workers/aspectSlopeCompositeWorker.js';
    }

    /**
     * Initialize composite layer
     * @param {string} accessToken - Mapbox access token
     * @param {mapboxgl.Map} map - Mapbox map instance
     * @param {string} palette - Color palette for aspect (default '8direction')
     */
    constructor (accessToken, map, palette = '8direction') {
        super(accessToken, map, { layerNamePrefix: 'composite' });
        this.palette = palette;
    }

    /**
     * Post message to worker with palette parameter
     */
    postMessageToWorker (worker, imageData, z, x, y) {
        worker.postMessage({ imageData, z, palette: this.palette });
    }

    /**
     * Process tile blob - decode DEM and compute composite aspect/slope colors
     * Uses base processImage from TerrainLayer with worker + fallback
     */
    async processTile (blob, z, x, y) {
        const blobUrl = URL.createObjectURL(blob);
        try {
            const img = await this.loadImage(blobUrl);
            return await this.processImage(img, z, x, y);
        } finally {
            URL.revokeObjectURL(blobUrl);
        }
    }

    /**
     * Fallback: Process image on main thread (if worker fails)
     * Computes composite colors from both aspect and slope
     */
    processImageMainThread = (img, z, x, y) => {
        const tileSize = 512;
        const canvas = document.createElement('canvas');
        canvas.width = tileSize;
        canvas.height = tileSize;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, tileSize, tileSize);
        const data = imageData.data;
        const dem = decodeDEM(data);
        const pixelMeters = calculatePixelMeters(z, tileSize);

        for (let pixelIdx = 0; pixelIdx < dem.length; pixelIdx++) {
            const elevation = dem[pixelIdx];
            if (!isValidElevation(elevation)) {
                const dataIdx = pixelIdx * 4;
                data[dataIdx] = 0;
                data[dataIdx + 1] = 0;
                data[dataIdx + 2] = 0;
                data[dataIdx + 3] = 0;
                continue;
            }
            const row = Math.floor(pixelIdx / tileSize);
            const col = pixelIdx % tileSize;
            const { gx, gy } = calculateSobel(dem, tileSize, tileSize, col, row);
            const slope = calculateSlope(gx, gy, pixelMeters);
            const aspect = calculateAspect(gx, gy);
            const [ r, g, b ] = this.getCompositeColor(aspect, slope);
            const dataIdx = pixelIdx * 4;
            data[dataIdx] = r;
            data[dataIdx + 1] = g;
            data[dataIdx + 2] = b;
            data[dataIdx + 3] = 255;
        }

        ctx.putImageData(imageData, 0, 0);
        return canvas;
    };

    /**
     * Get composite color based on aspect direction and slope angle
     * Palette: 8 directions × 4 slope ranges = 32 combinations
     */
    getCompositeColor = (aspect, slope) => {
        const palette = {
            'n': { 5: '#98b581', 20: '#8dc458', 40: '#84d600' },
            'ne': { 5: '#72a890', 20: '#3dab71', 40: '#00ab44' },
            'e': { 5: '#7c8ead', 20: '#5078b7', 40: '#0068c0' },
            'se': { 5: '#8c75a0', 20: '#77479d', 40: '#6c00a3' },
            's': { 5: '#b47ba1', 20: '#c04d9c', 40: '#ca009c' },
            'sw': { 5: '#cb8b8f', 20: '#e76f7a', 40: '#ff5568' },
            'w': { 5: '#c5a58a', 20: '#e2a66c', 40: '#ffab47' },
            'nw': { 5: '#bdbf89', 20: '#d6db5e', 40: '#f4fa00' },
        };
        const flatColor = '#a1a1a1';
        const normalizedAspect = ((aspect % 360) + 360) % 360;
        let direction;
        if (normalizedAspect >= 337.5 || normalizedAspect < 22.5) direction = 'n';
        else if (normalizedAspect >= 22.5 && normalizedAspect < 67.5) direction = 'ne';
        else if (normalizedAspect >= 67.5 && normalizedAspect < 112.5) direction = 'e';
        else if (normalizedAspect >= 112.5 && normalizedAspect < 157.5) direction = 'se';
        else if (normalizedAspect >= 157.5 && normalizedAspect < 202.5) direction = 's';
        else if (normalizedAspect >= 202.5 && normalizedAspect < 247.5) direction = 'sw';
        else if (normalizedAspect >= 247.5 && normalizedAspect < 292.5) direction = 'w';
        else direction = 'nw';
        let colorHex;
        if (slope < 0.625) {
            colorHex = flatColor;
        } else if (slope < 2.5) {
            colorHex = palette[direction][5];
        } else if (slope < 5) {
            colorHex = palette[direction][20];
        } else {
            colorHex = palette[direction][40];
        }
        const hex = colorHex.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return [ r, g, b ];
    };
}

export default CompositeLayer;
